<template>
  <!-- Render under body so public pages (booking + header/footer) match dashboard stacking -->
  <Teleport to="body">
  <div
    class="modal fade partner-pin-location-modal-root"
    :id="modalId"
    data-bs-backdrop="static"
    data-bs-keyboard="false"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-lg">
      <div class="modal-content bg-introduction rounded-3 padding-bottom-2">
        <div class="modal-header pb-0 border-bottom-0 padding-top-3 w-100 d-inline-block padding-left-3 padding-right-3">
          <h1 class="text-black mb-0 medium-family float-start text-size-14 text-full-14">
            Select Location
          </h1>
          <button
            type="button"
            class="btn-close mt-0 rounded-circle p-0 d-inline-block close-btn float-end m-0"
            @click="closeModal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body padding-2 position-relative">
          <div class="w-100 d-inline-block margin-top-2 position-relative">
            <div class="row">
              <div class="col-xl-10 col-12">
                <form class="position-relative booking-banner" @submit.prevent>
                  <input
                    type="text"
                    v-model="searchQuery"
                    @input="handleInput"
                    placeholder="Search for area, street name, landmark..."
                    class="bg-white box-shadow rounded-3 border-0 text-size-16 text-full-13 w-100"
                  />
                  <a class="position-absolute location-icon" href="javascript:void(0)">
                    <img v-lazy="require('@/assets/images/location-service-icon.svg')" alt="Location icon">
                  </a>
                  <i class="fa fa-angle-down position-absolute nav-color angle-right-icon" aria-hidden="true"></i>
                </form>
              </div>
              <div class="col-xl-2 col-12 mt-xl-0 mt-3 ps-xl-0">
                <div class="text-end margin-top-2">
                  <button
                    type="button"
                    class="btn btn-success px-4 py-2 text-white text-size-14 rounded-3 text-full-14"
                    @click="confirmLocationText"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
            <div v-if="predictions.length" class="predictions-container position-absolute w-100 margin-top-1 z-3">
              <div
                v-for="(suggestion, index) in predictions"
                :key="index"
                class="prediction-item padding-2 text-start bg-white border-bottom cursor-pointer text-full-13 text-size-14"
                @click="selectSuggestion(suggestion)"
              >
                {{ suggestion.description }}
              </div>
            </div>
          </div>

          <div class="w-100 d-inline-block margin-top-3 rounded-3 confirm-location-map partner-pin-map-wrap">
              <div :id="mapElementId" class="partner-pin-map-el"></div>
              <div
                v-if="!locationConfirmed"
                class="position-absolute start-50 translate-middle text-center text-white bg-dark bg-opacity-75 rounded-2"
                style="width: 310px; top: 75%; padding: 0 1rem !important;"
              >
                Move the map to set the exact position
              </div>
          </div>

          <div class="w-100 d-inline-block confirm-location-btn">
            <button
              type="button"
              class="btn bg-green px-4 py-2 text-white text-size-14 rounded-3 text-full-14 w-100 d-inline-block"
              :class="{ disabled: !locationConfirmed }"
              @click="confirmPinLocation"
            >
              Confirm Pin Location
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  </Teleport>
</template>

<script>
/* global google */
import { setModalAccessibleOpen, setModalAccessibleClosed } from '@/utils/modalAria';

export default {
  name: 'PartnerPinLocationModal',
  props: {
    modalId: {
      type: String,
      default: 'PartnerPinPickerModal',
    },
    mapElementId: {
      type: String,
      default: 'partner-pin-picker-map',
    },
  },
  data() {
    return {
      locationConfirmed: false,
      searchQuery: '',
      predictions: [],
      map: null,
      marker: null,
      pinLat: null,
      pinLng: null,
      pinAddress: '',
    };
  },
  methods: {
    open(seed = {}) {
      this.locationConfirmed = false;
      this.searchQuery = seed.address ? String(seed.address) : '';
      this.pinAddress = seed.address ? String(seed.address) : '';
      const lat = seed.lat != null ? Number(seed.lat) : null;
      const lng = seed.lng != null ? Number(seed.lng) : null;
      this.pinLat = Number.isFinite(lat) ? lat : null;
      this.pinLng = Number.isFinite(lng) ? lng : null;
      this.predictions = [];
      this.showModal();
      this.bootstrapPinMap();
    },
    waitForGoogleMaps(timeoutMs = 20000) {
      return new Promise((resolve, reject) => {
        if (window.google?.maps?.Map) {
          resolve();
          return;
        }
        const started = Date.now();
        const tick = () => {
          if (window.google?.maps?.Map) {
            resolve();
            return;
          }
          if (Date.now() - started > timeoutMs) {
            reject(new Error('Google Maps script timeout'));
            return;
          }
          window.setTimeout(tick, 50);
        };
        tick();
      });
    },
    async bootstrapPinMap() {
      try {
        await this.waitForGoogleMaps();
      } catch {
        this.$toast?.error('Could not load Google Maps. Check your connection and try again.');
        return;
      }
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.initMap());
        });
      });
    },
    showModal() {
      const modalElement = document.getElementById(this.modalId);
      if (!modalElement) return;
      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      document.body.classList.add('modal-open');
      setModalAccessibleOpen(modalElement);
    },
    closeModal() {
      const modalElement = document.getElementById(this.modalId);
      if (!modalElement) return;
      setModalAccessibleClosed(modalElement);
      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      document.body.classList.remove('modal-open');
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) backdrop.parentNode.removeChild(backdrop);
    },
    initMap() {
      const el = document.getElementById(this.mapElementId);
      if (!window.google?.maps?.Map || !el) return;

      if (this.marker) {
        this.marker.setMap(null);
        this.marker = null;
      }
      if (this.map) {
        google.maps.event.clearInstanceListeners(this.map);
        this.map = null;
      }

      let countryCode = '';
      let countryName = '';
      try {
        const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
        countryCode = (stored?.code || '').toString().trim();
        countryName = (stored?.name || '').toString().trim();
      } catch (_) {
        countryCode = '';
        countryName = '';
      }

      const fallbackLatLng = { lat: 25.2048, lng: 55.2708 };
      const hasSeed = Number.isFinite(Number(this.pinLat)) && Number.isFinite(Number(this.pinLng));
      const currentLatLng = hasSeed
        ? { lat: Number(this.pinLat), lng: Number(this.pinLng) }
        : fallbackLatLng;

      this.map = new google.maps.Map(el, {
        center: currentLatLng,
        zoom: hasSeed ? 14 : 6,
      });

      this.marker = new google.maps.Marker({
        position: currentLatLng,
        map: this.map,
        draggable: true,
      });

      google.maps.event.trigger(this.map, 'resize');
      this.map.setCenter(currentLatLng);

      this.pinLat = currentLatLng.lat;
      this.pinLng = currentLatLng.lng;

      if (!hasSeed && countryName) {
        const geocoder = new google.maps.Geocoder();
        const applyCountryCenter = (results, status) => {
          if (status === 'OK' && results?.[0] && this.map && this.marker) {
            const loc = results[0].geometry.location;
            this.map.setCenter(loc);
            this.map.setZoom(6);
            this.marker.setPosition(loc);
            this.pinLat = loc.lat();
            this.pinLng = loc.lng();
            google.maps.event.trigger(this.map, 'resize');
            return true;
          }
          return false;
        };
        const cc2 = countryCode && countryCode.length === 2 ? countryCode.toLowerCase() : '';
        const reqBiased = cc2
          ? { address: countryName, componentRestrictions: { country: cc2 } }
          : { address: countryName };
        geocoder.geocode(reqBiased, (results, status) => {
          if (applyCountryCenter(results, status)) return;
          if (cc2) {
            geocoder.geocode({ address: countryName }, (results2, status2) => {
              applyCountryCenter(results2, status2);
            });
          }
        });
      } else {
        this.locationConfirmed = true;
      }

      this.marker.addListener('dragend', (event) => {
        this.locationConfirmed = true;
        this.pinLat = event.latLng.lat();
        this.pinLng = event.latLng.lng();
        this.reverseGeocodePin(this.pinLat, this.pinLng, countryCode);
      });

      this.map.addListener('click', (event) => {
        this.marker.setPosition(event.latLng);
        this.pinLat = event.latLng.lat();
        this.pinLng = event.latLng.lng();
        this.locationConfirmed = true;
        this.reverseGeocodePin(this.pinLat, this.pinLng, countryCode);
      });
    },
    async handleInput() {
      if (this.searchQuery.trim().length > 1) {
        const result = await this.searchPlaces(this.searchQuery);
        if (result && result.predictions) {
          this.predictions = result.predictions;
        }
      } else {
        this.predictions = [];
      }
    },
    async searchPlaces(query) {
      try {
        let countryCode = '';
        let websiteName = '';
        try {
          const stored = JSON.parse(localStorage.getItem('selected_country') || 'null');
          countryCode = (stored?.code || '').toString().trim();
        } catch (_) {
          countryCode = '';
        }
        try {
          const storedWebsite = JSON.parse(localStorage.getItem('selected_website') || 'null');
          websiteName = (storedWebsite?.name || localStorage.getItem('website_name') || '').toString().trim();
        } catch (_) {
          websiteName = (localStorage.getItem('website_name') || '').toString().trim();
        }

        if (window.google?.maps?.places?.AutocompleteService) {
          const svc = new google.maps.places.AutocompleteService();
          const req = {
            input: query,
            ...(countryCode ? { componentRestrictions: { country: countryCode } } : {}),
          };
          const predictions = await new Promise((resolve) => {
            svc.getPlacePredictions(req, (preds) => resolve(preds || []));
          });
          return { predictions };
        }

        const response = await this.$axios.post('/search-places', {
          query,
          country_code: countryCode,
          website_name: websiteName,
        });
        return response.data;
      } catch (_) {
        return null;
      }
    },
    selectSuggestion(suggestion) {
      this.searchQuery = suggestion.description;
      this.pinAddress = suggestion.description;
      this.predictions = [];

      if (window.google && this.map && suggestion?.place_id) {
        const geocoder = new google.maps.Geocoder();
        geocoder.geocode({ placeId: suggestion.place_id }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            const loc = results[0].geometry.location;
            this.map.setCenter(loc);
            this.map.setZoom(15);
            if (this.marker) this.marker.setPosition(loc);
            this.pinLat = loc.lat();
            this.pinLng = loc.lng();
            this.locationConfirmed = true;
          }
        });
      }
    },
    confirmLocationText() {
      this.pinAddress = this.searchQuery;
    },
    confirmPinLocation() {
      if (!this.locationConfirmed || this.pinLat == null || this.pinLng == null) {
        this.$toast?.error('Please select a location on the map.');
        return;
      }
      const address = String(this.pinAddress || this.searchQuery || '').trim();
      if (!address) {
        this.$toast?.error('Please select an address.');
        return;
      }
      this.$emit('confirmed', {
        address,
        lat: Number(this.pinLat),
        lng: Number(this.pinLng),
      });
      this.closeModal();
    },
    reverseGeocodePin(lat, lng, countryCode = '') {
      if (!window.google?.maps?.Geocoder) return;
      const geocoder = new google.maps.Geocoder();
      const latLng = { lat: Number(lat), lng: Number(lng) };
      geocoder.geocode(
        {
          location: latLng,
          ...(countryCode ? { region: countryCode } : {}),
        },
        (results, status) => {
          if (status === 'OK' && results && results[0]) {
            this.pinAddress = results[0].formatted_address || '';
            this.searchQuery = this.pinAddress;
          }
        }
      );
    },
  },
};
</script>

<style scoped>
.partner-pin-map-wrap {
  position: relative;
  overflow: visible;
}
.partner-pin-map-el {
  width: 100%;
  height: 100%;
  min-height: 280px;
}

/* Mobile polish */
@media (max-width: 576px) {
  .partner-pin-map-el {
    min-height: 240px;
  }
  .predictions-container {
    max-height: 220px;
    overflow: auto;
  }
}
</style>
